/**
 * LE CATALOGUE EFFECTIF D'UN CHAMP A CHOIX, pour le code qui n'est pas declare.
 *
 * LE PROBLEME QU'IL FERME. Les valeurs d'un champ `select` vivent dans
 * `taki.fields.json`, et l'ecran generique les lit tout seul : aucune app n'a a
 * s'en occuper. Une surface CANVAS, elle, est du React ordinaire : la fenetre
 * « + Ajouter » de Ma semaine doit proposer les memes categories que la fiche
 * du Journal, et rien ne les lui donne. Les recopier dans le composant ferait
 * deux listes qui divergeront au premier reglage.
 *
 * ET L'ORG A LE DERNIER MOT. Le fichier du bundle est le MODELE ; une
 * organisation peut en tenir sa propre version (`resoudreDeclaration`,
 * `@taki/app-runtime`), et c'est elle que l'ecran monte. Lire le seul fichier
 * du bundle ferait donc mentir la fenetre des qu'un client regle sa liste dans
 * le Studio : on lit d'abord l'INSTANCE de l'org, le bundle en repli.
 *
 * ON NE VALIDE RIEN ICI, et c'est voulu : l'instance est deja validee par
 * `resoudreDeclaration` avant que l'ecran ne monte, et une instance illisible y
 * retombe sur la native. Ce module ne fait que LIRE, defensivement, et rendre
 * une liste vide quand il ne trouve pas : une liste deroulante vide se voit,
 * une exception ferait tomber la semaine entiere.
 */

import { jeuxDesInstances, type InstancesDeclarations } from '@taki/app-runtime';
import { alignerSurLesJeux } from '@taki/data-model';
import champsDuBundle from './taki.fields.json';
import ecransDuBundle from './taki.screens.json';

/** L'id de l'app, celui de `taki.app.json` et des clefs d'instances. */
const APP_ID = 'timesheet';

/** Une valeur proposee : ce qui est STOCKE, et le mot qui s'affiche. */
export interface OptionDeCatalogue {
  value: string;
  label: string;
}

/** Vrai pour un objet ordinaire, seul endroit ou l'on descend. */
function estObjet(valeur: unknown): valeur is Record<string, unknown> {
  return typeof valeur === 'object' && valeur !== null && !Array.isArray(valeur);
}

/** UN CHAMP A CHOIX du jeu : sa clef, son mot, et ses valeurs. */
export interface ChampAChoix {
  key: string;
  label: string;
  options: readonly OptionDeCatalogue[];
}

/**
 * Les champs du jeu `family`/`type` dans la declaration EFFECTIVE, ou rien.
 * Un jeu que l'org a regle dans une AUTRE app (les taches de Projets) se lit
 * ici tel qu'elle l'a regle : un jeu partage a une seule definition
 * (`alignerSurLesJeux`, `docs/socle/jeu-listes.md`).
 */
function champsDuJeu(
  instances: InstancesDeclarations | undefined,
  family: string,
  type: string,
): readonly unknown[] {
  const brut = instances?.[APP_ID];
  const declaration = alignerSurLesJeux(
    {
      champs: estObjet(brut?.champs) ? brut.champs : (champsDuBundle as unknown),
      ecrans: estObjet(brut?.ecrans) ? brut.ecrans : (ecransDuBundle as unknown),
    },
    jeuxDesInstances(instances),
  );
  const champs = estObjet(declaration.champs) ? declaration.champs : {};
  const jeux = champs['jeux'];
  if (!Array.isArray(jeux)) return [];
  const jeu = jeux.find(
    (candidat) => estObjet(candidat) && candidat['family'] === family && candidat['type'] === type,
  );
  return estObjet(jeu) && Array.isArray(jeu['champs']) ? jeu['champs'] : [];
}

/** Les valeurs d'un champ, telles que la declaration effective les porte. */
function optionsDe(champ: Record<string, unknown>): readonly OptionDeCatalogue[] {
  if (!Array.isArray(champ['options'])) return [];
  return champ['options'].flatMap((option) =>
    estObjet(option) && typeof option['value'] === 'string' && typeof option['label'] === 'string'
      ? [{ value: option['value'], label: option['label'] }]
      : [],
  );
}

/**
 * LES CHAMPS A CHOIX D'UN JEU, dans l'ordre ou la declaration les pose.
 *
 * ON NE NOMME PAS LE CHAMP, ET C'EST TOUT L'INTERET. Le modele de Timesheet
 * n'en a plus aucun depuis le 14/10/2026 (la saisie vise une tache) ; une
 * agence audiovisuelle appelle le sien `categorie` (tournage, montage,
 * SEA...). Une fenetre qui citerait l'une des deux clefs
 * marcherait pour un client et rendrait une case morte chez l'autre, sans
 * qu'aucun test ne le voie. Elle demande donc « les choix de ce jeu », et
 * rend ce que CETTE organisation a declare.
 */
export function champsAChoixDuJeu(
  instances: InstancesDeclarations | undefined,
  family: string,
  type: string,
): readonly ChampAChoix[] {
  return champsDuJeu(instances, family, type).flatMap((champ) => {
    if (!estObjet(champ) || champ['type'] !== 'select') return [];
    if (typeof champ['key'] !== 'string' || typeof champ['label'] !== 'string') return [];
    const options = optionsDe(champ);
    return options.length === 0 ? [] : [{ key: champ['key'], label: champ['label'], options }];
  });
}

/** Le mot d'une valeur stockee, ou la valeur elle-meme quand le catalogue l'ignore. */
export function motDeLaValeur(
  options: readonly OptionDeCatalogue[],
  valeur: string | undefined,
): string {
  if (valeur === undefined || valeur === '') return '';
  return options.find((option) => option.value === valeur)?.label ?? valeur;
}

/**
 * LE CHAMP DECLARE QUI TITRE UNE SAISIE, tel que la declaration EFFECTIVE le
 * nomme : le titre de la liste de l'ecran (`vb['core.liste'].title`). Sur le
 * modele, ce titre est la tache, un champ NATIF (`socle:taskId`, 14/10/2026) :
 * aucun champ declare ne le porte, et la fonction rend `null`.
 * La semaine titre ses creneaux par le meme champ que le Journal, y compris
 * quand une org le change dans le Studio. `null` quand rien ne se lit.
 */
export interface TitreDuJeu {
  key: string;
  label: string;
  /** Les choix du champ, quand il en a : le titre est alors le mot du choix. */
  options: readonly OptionDeCatalogue[];
  /** Le mot d'une saisie qui n'a pas de valeur (« Sans catégorie »). */
  vide: string;
}

export function titreDuJeu(
  instances: InstancesDeclarations | undefined,
  family: string,
  type: string,
): TitreDuJeu | null {
  const brut = instances?.[APP_ID]?.ecrans;
  const fichier = estObjet(brut) ? brut : (ecransDuBundle as unknown as Record<string, unknown>);
  const ecran = Array.isArray(fichier['ecrans']) ? fichier['ecrans'][0] : undefined;
  if (!estObjet(ecran)) return null;
  /* Le detail d'une porte du jeu gagne sur celui de l'ecran (meme regle que le socle). */
  const portes = Array.isArray(ecran['portes']) ? ecran['portes'] : [];
  const porte = portes.find(
    (p) => estObjet(p) && estObjet(p['jeu']) && p['jeu']['family'] === family && p['jeu']['type'] === type && estObjet(p['vb']),
  );
  const vb = estObjet(porte) && estObjet(porte['vb']) ? porte['vb'] : ecran['vb'];
  const liste = estObjet(vb) ? vb['core.liste'] : undefined;
  const titre = estObjet(liste) ? liste['title'] : undefined;
  if (!estObjet(titre) || typeof titre['champ'] !== 'string') return null;
  const key = titre['champ'];
  const champ = champsDuJeu(instances, family, type).find((c) => estObjet(c) && c['key'] === key);
  if (!estObjet(champ)) return null;
  const label = typeof champ['label'] === 'string' ? champ['label'] : key;
  const vide = typeof titre['vide'] === 'string' && titre['vide'] !== '' ? titre['vide'] : `Sans ${label.toLocaleLowerCase('fr-FR')}`;
  return { key, label, options: optionsDe(champ), vide };
}
